import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaFileInvoiceDollar } from "react-icons/fa";
import AbsatAppsListItem from "./AbsatAppsListItem.jsx";
import { useTranslation } from "react-i18next";
import { useAppsHome } from "../../data/AbsatApps.jsx";
import { useSelector } from "react-redux";
import { userHasAppAccess } from "../../utils/appAccess.js";

export const AbsatAppsList = () => {
  const [apps, setApps] = useState([]);
  const [redirectInfo, setRedirectInfo] = useState(null);
  const { t, i18n } = useTranslation("global");
  const authUser = useSelector((state) => state.auth?.user);
  const userRaw = localStorage.getItem("user");
  const tenantRaw = localStorage.getItem("tenant");
  const _user = useMemo(() => {
    if (authUser) return authUser;
    if (!userRaw) return null;
    try {
      return JSON.parse(userRaw);
    } catch {
      return null;
    }
  }, [authUser, userRaw]);
  const hasResolvedUserApps = Array.isArray(_user?.my_apps);
  const hasAssignedApps = hasResolvedUserApps && _user.my_apps.length > 0;
  const tenantForRoute =
    useMemo(() => {
      if (!tenantRaw) return null;
      try {
        return JSON.parse(tenantRaw)?.username || null;
      } catch {
        return null;
      }
    }, [tenantRaw]) || window.location.pathname.split("/").filter(Boolean)[0];
  const settingsUrl = tenantForRoute ? `/${tenantForRoute}/settings` : "/notfound";
  const vatFilingReportUrl = tenantForRoute ? `/${tenantForRoute}/vat-filing-report` : "/notfound";
  const { apps: APPS, loadingApps } = useAppsHome();
  const filteredApps = useMemo(() => {
    if (!_user || !hasResolvedUserApps || !Array.isArray(APPS)) return [];
    if (_user.my_apps.length === 0) return [];

    return APPS.filter((app) => _user.my_apps.includes(app._id)).reverse();
  }, [_user, APPS, hasResolvedUserApps]);
  const canViewVatFilingReport = userHasAppAccess(_user, APPS, "sales")
    && userHasAppAccess(_user, APPS, "purchases");
  const allowAutoRedirect =
    String(process.env.REACT_APP_APPS_DASHBOARD_AUTO_REDIRECT || "")
      .trim()
      .toLowerCase() === "true";
  const redirectedRef = useRef(false);
  const redirectTimeoutRef = useRef(null);
  const buildAppUrl = useCallback((app, tenantUsername, token) => {
    const normalizedBaseUrl = String(app?.baseUrl || "").trim().replace(/\/+$/, "");
    if (!normalizedBaseUrl || !tenantUsername || !token) return null;
    return `${normalizedBaseUrl}/${tenantUsername}/token/${encodeURIComponent(token)}`;
  }, []);
  const buildSupportAdminUrl = useCallback((tenantUsername, token) => {
    if (!tenantUsername || !token) return null;
    return `https://support-admin.absat-erp.com/${tenantUsername}/token/${encodeURIComponent(token)}`;
  }, []);
  const isSupportApp = useCallback((app) => {
    const normalizedBaseUrl = String(app?.baseUrl || "").trim().replace(/\/+$/, "");
    const appNameText = `${app?.name || ""} ${app?.titleAr || ""} ${app?.titleEn || ""}`
      .toLowerCase()
      .trim();
    return (
      appNameText.includes("support") ||
      appNameText.includes("الدعم") ||
      (normalizedBaseUrl.includes("support.absat-erp.com") &&
        !normalizedBaseUrl.includes("support-admin.absat-erp.com"))
    );
  }, []);
  const buildEffectiveAppUrl = useCallback((app, tenantUsername, token, user) => {
    if (isSupportApp(app) && user?.supportAdminEnabled === true) {
      return buildSupportAdminUrl(tenantUsername, token);
    }
    return buildAppUrl(app, tenantUsername, token);
  }, [buildAppUrl, buildSupportAdminUrl, isSupportApp]);
  const getAppTitle = (app, lang) =>
    lang === "ar"
      ? app?.titleAr || app?.titleEn || app?.name
      : app?.titleEn || app?.titleAr || app?.name;
  const isCurrentOriginApp = useCallback((url) => {
    try {
      return new URL(url).origin === window.location.origin;
    } catch {
      return false;
    }
  }, []);
  const startRedirect = useCallback((systemName, url) => {
    if (!url) return;
    setRedirectInfo({ systemName, url });
    redirectTimeoutRef.current = setTimeout(() => {
      window.location.replace(url);
    }, 20);
  }, []);
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (!_user) return;
    if (!hasResolvedUserApps) return;
    const custApps = _user.my_apps;

    const demoLocalMatch = (v) =>
      String(v || "")
        .trim()
        .toLowerCase()
        .includes("demo-local");
    const isDemoLocal =
      demoLocalMatch(_user?.username) ||
      demoLocalMatch(_user?.email) ||
      demoLocalMatch(_user?.name) ||
      demoLocalMatch(JSON.parse(localStorage.getItem("tenant"))?.username);
    const isLocalDevHost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.port === "3000";

    const lang = String(i18n.language || localStorage.getItem("i18nextLng") || "en")
      .toLowerCase()
      .startsWith("ar")
      ? "ar"
      : "en";

    if (!redirectedRef.current && isDemoLocal && !isLocalDevHost) {
      redirectedRef.current = true;
      const systemName = lang === "ar" ? "ديمو محلي" : "Demo Local";
      startRedirect(systemName, "http://localhost:3000");
      return;
    }

    const tenantUsername =
      JSON.parse(localStorage.getItem("tenant"))?.username ||
      window.location.pathname.split("/")[1] ||
      _user?.username ||
      "admin";
    const token = localStorage.getItem("token");
    if (!redirectedRef.current) {
      // ✅ إذا لم يكن هناك تطبيقات
      if (custApps.length === 0) {
        if (_user?.supportAdminEnabled === true && token) {
          const systemName = lang === "ar" ? "الدعم (الإدارة)" : "Support (Admin)";
          const url = buildSupportAdminUrl(tenantUsername, token);
          if (url && !isCurrentOriginApp(url)) {
            redirectedRef.current = true;
            startRedirect(systemName, url);
            return;
          }
        }
        // ✅ للـ demo-local: لا نوجه إلى الدعم، نبقى في الداشبورد
        if (isLocalDevHost) {
          setApps([]);
          return;
        }
        // ✅ للمستخدمين العاديين: نوجه إلى الدعم
        redirectedRef.current = true;
        const systemName = lang === "ar" ? "الدعم" : "Support";
        const url = buildAppUrl(
          { baseUrl: "https://support.absat-erp.com" },
          tenantUsername,
          token
        );

        startRedirect(systemName, url);
        return;
      }
    }

    if (!_user || !APPS) return;

    const updatedApps = filteredApps;
    const defaultApp = updatedApps.find((app) => app?._id === _user?.default_app_id);

    if (allowAutoRedirect && !redirectedRef.current && defaultApp?.status === "done" && tenantUsername && token) {
      const url = buildEffectiveAppUrl(defaultApp, tenantUsername, token, _user);
      if (url && !isCurrentOriginApp(url)) {
        redirectedRef.current = true;
          const systemName = getAppTitle(defaultApp, lang);

        startRedirect(systemName, url);
        return;
      }
    }

    // ✅ إذا كان هناك تطبيق واحد فقط
    if (!redirectedRef.current && custApps.length === 1) {
      const onlyApp = updatedApps[0];

      if (onlyApp?.status === "done" && tenantUsername && token) {
        const url = buildEffectiveAppUrl(onlyApp, tenantUsername, token, _user);
        if (url && !isCurrentOriginApp(url)) {
          redirectedRef.current = true;

          const systemName = getAppTitle(onlyApp, lang);

          startRedirect(systemName, url);
          return;
        }
      }
    }

    if (isLocalDevHost) {
      setApps(updatedApps);
      return;
    }

    setApps(updatedApps);
  }, [
    _user,
    APPS,
    allowAutoRedirect,
    buildAppUrl,
    buildEffectiveAppUrl,
    buildSupportAdminUrl,
    filteredApps,
    hasResolvedUserApps,
    i18n.language,
    isCurrentOriginApp,
    startRedirect,
  ]);

  const pendingAutoRedirect = useMemo(() => {
    if (redirectInfo || redirectedRef.current) return false;
    if (!_user || !hasResolvedUserApps || !Array.isArray(APPS)) return false;

    const custApps = _user.my_apps;
    const token = localStorage.getItem("token");
    const tenantUsername =
      (() => {
        try {
          return JSON.parse(localStorage.getItem("tenant"))?.username;
        } catch {
          return null;
        }
      })() ||
      window.location.pathname.split("/")[1] ||
      _user?.username ||
      "admin";

    if (custApps.length === 0) {
      return Boolean(token && tenantUsername);
    }

    const updatedApps = filteredApps;
    if (allowAutoRedirect) {
      const defaultApp = updatedApps.find((app) => app?._id === _user?.default_app_id);
      const defaultUrl = buildEffectiveAppUrl(defaultApp, tenantUsername, token, _user);
      if (defaultApp?.status === "done" && defaultUrl && !isCurrentOriginApp(defaultUrl)) {
        return true;
      }
    }

    if (custApps.length !== 1) return false;

    const onlyApp = updatedApps[0];
    const onlyAppUrl = buildEffectiveAppUrl(onlyApp, tenantUsername, token, _user);
    return Boolean(onlyApp?.status === "done" && onlyAppUrl && !isCurrentOriginApp(onlyAppUrl));
  }, [
    _user,
    APPS,
    allowAutoRedirect,
    buildEffectiveAppUrl,
    filteredApps,
    hasResolvedUserApps,
    isCurrentOriginApp,
    redirectInfo,
  ]);

  const appsToRender = hasResolvedUserApps ? filteredApps : apps;

  return (
    <>
      {redirectInfo ? (
        <div className="text-center w-full py-10 text-gray-700 text-xl flex flex-col items-center gap-3">
          <span className="inline-block w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
           <div className="mt-1">
            {t("auth.redirectingToSystem", { systemName: redirectInfo.systemName })}
          </div>
        </div>
      ) : (
      loadingApps || pendingAutoRedirect || !hasResolvedUserApps || (hasAssignedApps && appsToRender.length === 0) ? (
        <p className="text-center w-full py-10 text-gray-500">
          <span className="inline-block w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        </p>
      ) : appsToRender.length === 0 ? (
        <p className="text-center w-full py-10 text-gray-500 text-xl">
          {t("Dashboard.appnotfound")}
        </p>
      ) : (
        <div
          className="grid w-full max-w-[900px] mx-auto items-center justify-items-stretch gap-3 mt-6 mb-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
        >
          {appsToRender.map((app) => (
            <AbsatAppsListItem
              key={app.name}
              user={_user}
              app={app}
              onRedirect={startRedirect}
            />
          ))}
          <Link
            to={settingsUrl}
            className="category py-5 hover:bg-violet-50 h-[110px] transition-[0.7s] border border-[#B8AAEB] rounded-md flex flex-col justify-center items-center text-center gap-1 w-full"
          >
            <img
              src="/assets/icons/settings.png"
              className="max-w-14"
              style={{
                filter:
                  "brightness(0) saturate(100%) invert(14%) sepia(38%) saturate(1840%) hue-rotate(223deg) brightness(86%) contrast(96%)",
              }}
              alt={t("menu.Settings")}
            />
            <h3 className="font-bold text-[17px] text-[#251658]">{t("menu.Settings")}</h3>
          </Link>
          {canViewVatFilingReport && (
          <Link
            to={vatFilingReportUrl}
            className="category py-5 hover:bg-violet-50 h-[110px] transition-[0.7s] border border-[#B8AAEB] rounded-md flex flex-col justify-center items-center text-center gap-1 w-full"
          >
            <FaFileInvoiceDollar className="w-12 h-12 text-[#251658]" />
            <h3 className="font-bold text-[17px] text-[#251658]">{t("menu.VatFilingReport")}</h3>
          </Link>
          )}
        </div>
      )
      )}
    </>
  );
};
